'use client'

import { DotPattern } from '@/components/home/DotPattern'
import { BrandLogo } from '@/components/ui/brand-logo'
import Link from 'next/link'
import type React from 'react'

interface AuthLayoutProps {
  children: React.ReactNode
  /** Título do card de autenticação */
  title: string
  /** Subtítulo/descrição */
  description?: string
  /** Rodapé do card (ex: "Já tem conta? Entrar") */
  footer?: React.ReactNode
}

/**
 * Layout padrão para todas as telas de autenticação.
 * Replica o DNA visual da Home: warm gradient, glass card,
 * blobs animados, animate-in-up e BrandLogo unificado.
 */
export function AuthLayout({ children, title, description, footer }: AuthLayoutProps) {
  return (
    <div className="dark w-full h-full">
      <div className="auth-bg">
        {/* Radial gradients from the reference */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,99,235,0.15),transparent_60%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(124,58,237,0.1),transparent_60%)] pointer-events-none" />

        {/* Concentric circles from the reference */}
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-white/[0.03] pointer-events-none" />
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/[0.03] pointer-events-none" />
        <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/[0.03] pointer-events-none" />

        {/* Cube texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')",
          }}
        />

        {/* Dot pattern overlay */}
        <DotPattern className="opacity-[0.03] text-white pointer-events-none" />

        {/* Card central */}
        <div className="relative z-10 w-full max-w-md animate-in-up">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex flex-col items-center gap-1">
              <BrandLogo size="md" showDot showTagline href="/" />
            </div>
          </div>

          {/* Glass card - Replaced with glass-dark */}
          <div className="glass-dark rounded-2xl border border-white/10 shadow-2xl shadow-black/50 p-8 backdrop-blur-md">
            {/* Header do form */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold tracking-tight text-white mb-1">{title}</h1>
              {description && <p className="text-sm text-slate-400 font-medium">{description}</p>}
            </div>

            {/* Conteúdo injetado (form, inputs, botões) */}
            {children}

            {/* Footer opcional */}
            {footer && <div className="mt-6 text-center text-sm text-slate-400">{footer}</div>}
          </div>

          {/* Link de volta */}
          <p className="text-center text-xs text-slate-500 mt-6">
            <Link href="/" className="hover:text-blue-400 transition-colors">
              ← Voltar para a página inicial
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
